import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import * as contextVariableService from '../../data/services/contextVariableService';
import * as addContextVariableController from '../controllers/addContextVariableController';
import * as editContextVariableController from '../controllers/editContextVariableController';
import ContextVariableCard from '../components/ContextVariableCard';
import ContextVariableForm from '../components/ContextVariableForm';
import './ContextVariablesScreen.css';

const ContextVariablesScreen = () => {
  const { user, organization } = useAppContext();
  const [contextVariables, setContextVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);

  useEffect(() => {
    loadContextVariables();
  }, [user, organization]);

  const loadContextVariables = async () => {
    try {
      setLoading(true);
      const variables = await contextVariableService.getAllContextVariables();
      setContextVariables(variables.map(v => v.toJSON()));
    } catch (error) {
      console.error('Error loading context variables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (data) => {
    try {
      await addContextVariableController.addContextVariable(data);
      // Reload the list to ensure we have the latest data from the server
      await loadContextVariables();
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding context variable:', error);
      alert('Failed to add context variable. Please try again.');
    }
  };

  const handleUpdate = (updatedVariable) => {
    setContextVariables(prev =>
      prev.map(v => v.id === updatedVariable.id ? updatedVariable : v)
    );
  };

  const handleEdit = async (data) => {
    try {
      const updated = await editContextVariableController.editContextVariable(editingVariable.id, data);
      handleUpdate(updated);
      setEditingVariable(null);
    } catch (error) {
      console.error('Error saving context variable:', error);
      alert('Failed to save context variable. Please try again.');
    }
  };

  const handleStartEdit = (variable) => {
    setShowAddForm(false);
    setEditingVariable(variable);
  };

  const handleCancelEdit = () => {
    setEditingVariable(null);
  };

  const handleDelete = (id) => {
    setContextVariables(prev => prev.filter(v => v.id !== id));
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="context-variables-screen">
        <div className="loading">Loading context variables...</div>
      </div>
    );
  }

  return (
    <div className="context-variables-screen">
      <header className="context-variables-header">
        <h1 className="context-variables-title">
          Context Variables
        </h1>
        <p className="context-variables-subtitle">
          Context variables are reusable context snippets you can add to prompts when copying. Select context variables to automatically include them in your copied prompt text.
        </p>
      </header>

      <div className="context-variables-actions">
        {!showAddForm && !editingVariable && (
          <button
            className="add-context-variable-button"
            onClick={() => setShowAddForm(true)}
          >
            <FaPlus className="add-icon" />
            <span>Add Context Variable</span>
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="add-context-variable-form-container">
          <h2 className="add-form-title">Add Context Variable</h2>
          <ContextVariableForm
            onSave={handleAdd}
            onCancel={handleCancelAdd}
            isGlobalEnabled={!!organization}
          />
        </div>
      )}

      {editingVariable && (
        <div className="add-context-variable-form-container">
          <h2 className="add-form-title">Edit Context Variable</h2>
          <ContextVariableForm
            initialData={editingVariable}
            onSave={handleEdit}
            onCancel={handleCancelEdit}
            isGlobalEnabled={!!organization}
          />
        </div>
      )}

      <div className="context-variables-grid">
        {contextVariables.length > 0 ? (
          contextVariables.map((variable) => (
            <ContextVariableCard
              key={variable.id}
              contextVariable={variable}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onEdit={handleStartEdit}
            />
          ))
        ) : (
          <div className="empty-message">
            {showAddForm || editingVariable
              ? 'Fill out the form above to create your first context variable.'
              : 'No context variables yet. Click "Add Context Variable" to create one.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextVariablesScreen;

